import { TaskVideoPreview } from "../features/tasks/components/TaskVideoEditor";
import { AgentTaskControls } from '../features/agency/AgentTaskControls';
import { getTask } from '../features/tasks';
import { AgencySubmissions } from '../features/agency/AgencySubmissions';
import { AgentMessagePool } from '../features/agency/AgentMessagePool';
import { AgentDraftMessageImport } from '../features/agency/AgentDraftMessageImport';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as api from '../features/agency/service';
import {
  Badge,
  button,
  primary,
  input,
  Panel,
  Notice,
  Pager,
  useCan,
  useFilters,
  useResource,
  errorText,
} from '../features/agency/ui';
import { formatDateTime } from '../lib/format';
import { resolveImagePreviewUrl, resolveAdminImagePreviewUrl } from '../lib/upload/cos';
// Older task APIs may omit agent_name; resolve only missing names with existing permissions.
function useTaskMerchantNames(items: api.AgentTask[] = []) {
  const canRead = useCan('agents.view');
  const missing = [...new Set(items.filter(item => !item.agent_name?.trim()).map(item => item.agent_id))].sort((a, b) => a - b);
  const key = missing.join(',');
  const resource = useResource(async () => {
    if (!canRead || !missing.length) return { names: {} as Record<number, string>, failed: false };
    const results = await Promise.allSettled(missing.map(id => api.getAgent(id)));
    const names: Record<number, string> = {};
    for (const [index, result] of results.entries()) {
      if (result.status === 'fulfilled' && result.value.id === missing[index] && result.value.name?.trim()) names[missing[index]] = result.value.name.trim();
    }
    return { names, failed: missing.some(id => !names[id]) };
  }, [key, canRead]);
  return {
    name: (item: api.AgentTask) => item.agent_name?.trim() || resource.data?.names[item.agent_id] || (canRead && resource.loading ? '正在加载商户名称…' : '商户名称暂不可用'),
    failed: resource.data?.failed,
    reload: resource.reload,
  };
}

export function AgentTasksPage({ review = false }: { review?: boolean }) {
  const { q, set, page } = useFilters();
  const r = useResource(
    () =>
      api.listAgentTasks({
        page,
        page_size: 20,
        review: review ? 'true' : '',
        agent_id: q.get('agent_id') ?? '',
        status: review ? 'pending_review' : '',
        execution_status: review ? '' : (q.get('execution_status') ?? q.get('status') ?? ''),
        search: q.get('search') ?? '',
      }),
    [q.toString(), review],
  );
  const merchants = useTaskMerchantNames(r.data?.items);
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        {review
          ? '商户任务内容发布前审核。会员已领取任务继续使用领取时的版本与奖励快照。'
          : '管理商户任务的线上状态；已结算仅表示会员任务奖励已发放完成，商户商务结算暂未开放。'}
      </p>
      <Notice error={r.error} />
      {merchants.failed && <p role="alert" className="text-sm text-amber-700">部分商户名称加载失败。<button className="ml-2 underline" onClick={merchants.reload}>重试加载名称</button></p>}
      {!review && r.data?.execution_counts && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[["all", "全部任务"], ["active", "进行中"], ["ended", "已结束"], ["settled", "已结算（会员奖励）"]].map(([key, label]) => <button key={key} className="rounded-lg border border-gray-200 bg-white p-4 text-left" onClick={() => set('execution_status', key === 'all' ? '' : key)}><p className="text-xs text-gray-500">{label}</p><p className="text-2xl font-semibold mt-2">{r.data?.execution_counts?.[key] ?? 0}</p></button>)}</div>}

      <Panel>
        <div className="flex flex-wrap gap-3">
          <input
            aria-label="搜索商户任务"
            className={input}
            placeholder="搜索任务"
            value={q.get('search') ?? ''}
            onChange={(e) => set('search', e.target.value)}
          />
          <input
            aria-label="商户 ID"
            className={input}
            placeholder="商户 ID"
            value={q.get('agent_id') ?? ''}
            onChange={(e) => set('agent_id', e.target.value)}
          />
          {!review && (
            <select
              aria-label="任务状态"
              className={input}
              value={q.get('execution_status') ?? q.get('status') ?? ''}
              onChange={(e) => set('execution_status', e.target.value)}
            >
              <option value="">全部任务状态</option>
              {[
                'draft',
                'pending_review',
                'approved',
                'rejected',
                'paused',
                'active',
                'ended',
                'settled',
                'offline',
              ].map((s) => (
                <option key={s} value={s}>
                  {
                    {
                      draft: '草稿',
                      pending_review: '待审核',
                      approved: '待开始',
                      active: '进行中',
                      ended: '已结束',
                      settled: '已结算（会员奖励）',
                      rejected: '已驳回',
                      paused: '已暂停',
                      offline: '已下架',
                    }[s]
                  }
                </option>
              ))}
            </select>
          )}
          <button className={button} onClick={r.reload}>
            刷新
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm text-left">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                {[
                  '任务',
                  '商户',
                  '任务状态',
                  '奖励 / 名额',
                  '更新时间',
                  '操作',
                ].map((t) => (
                  <th className="p-3" key={t}>
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.data?.items.map((t) => (
                <tr key={t.id} className="border-b border-gray-100">
                  <td className="p-3">
                    {t.payload.title}
                  </td>
                  <td className="p-3 break-words max-w-64">{merchants.name(t)}</td>
                  <td className="p-3">
                    <Badge value={t.execution_status || t.status} />
                  </td>
                  <td className="p-3">
                    ¥{((t.payload.reward_amount_minor ?? 0) / 100).toFixed(2)} /{' '}
                    {t.payload.max_assignments}
                  </td>
                  <td className="p-3">{formatDateTime(t.updated_at)}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-2 min-w-48">
                    <Link
                      className="text-primary"
                      to={`/${review ? 'agent-task-reviews' : 'agent-tasks'}/${t.id}`}
                    >
                      {review ? '审核详情' : '查看 / 控制'}
                    </Link>
                    {!review && <AgentTaskControls task={t} onChanged={r.reload} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.loading ? (
            <p className="py-8 text-center text-gray-400">加载中…</p>
          ) : (
            !r.data?.items.length && (
              <p className="py-8 text-center text-gray-400">
                {review ? '暂无待审核任务' : '暂无商户任务'}
              </p>
            )
          )}
        </div>
        <Pager
          total={r.data?.total ?? 0}
          page={page}
          change={(v) => set('page', String(v))}
        />
      </Panel>
    </div>
  );
}
const fieldLabels: Record<string, string> = {
  title: '任务标题',
  description: '任务说明',
  description_title: '说明标题',
  category: '分类',
  task_type: '任务类型',
  platform: '平台',
  reward_amount_minor: '奖励（分）',
  max_assignments: '名额',
  assignment_timeout_seconds: '领取超时（秒）',
  require_review: '会员提交需审核',
  rules: '任务规则',
  review_rules: '审核规则',
  steps: '任务步骤',
  start_at: '开始时间',
  end_at: '结束时间',
  icon_url: '图标',
  download_app: '下载应用',
  app_info: '应用信息',
  message_config: 'iMessage 配置',
  tags: '标签',
  required_screenshots: '截图要求',
  completion_screenshot_count: '完成截图数量',
  submission_requirements: '提交要求',
  remarks: '任务备注',
};
function shown(value: unknown) {
  if (value == null || value === '') return '—';
  return typeof value === 'object'
    ? JSON.stringify(value, null, 2)
    : typeof value === 'boolean'
      ? value
        ? '是'
        : '否'
      : String(value);
}
function TextContent({ value }: { value?: string }) {
  return (
    <div className="whitespace-pre-wrap break-words text-sm text-gray-700">
      {value ? value.replace(/<[^>]*>/g, '') : '—'}
    </div>
  );
}
export function AgentTaskDetailPage() {
  const id = Number(useParams().id);
  const r = useResource(() => api.getAgentTask(id), [id]);
  const revisions = useResource(() => api.taskRevisions(id), [id]);
  const reviews = useResource(() => api.taskReviews(id), [id]);
  const canReview = useCan('agent_tasks.review');
  const canControl = useCan('agent_tasks.control');
  const display = useResource(
    () =>
      canControl && r.data?.formal_task_id
        ? getTask(r.data.formal_task_id)
        : Promise.resolve(undefined),
    [r.data?.formal_task_id, canControl],
  );
  const [note, setNote] = useState('');
  const [internal, setInternal] = useState('');
  const [compare, setCompare] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const requestKey = useRef<string>('');
  const [sort, setSort] = useState('0');
  const [recommended, setRecommended] = useState(false);
  useEffect(() => {
    if (display.data) {
      setSort(String(display.data.sort_order));
      setRecommended(display.data.is_recommended);
    }
  }, [display.data]);
  useEffect(() => {
    setNote('');
    setInternal('');
    setCompare('');
    setDone('');
    setError('');
    requestKey.current = '';
  }, [id]);
  const t = r.data;
  const merchants = useTaskMerchantNames(t ? [t] : []);
  const lastApprovedID = reviews.data?.items.find(
    (x) =>
      x.decision === 'approved' && x.revision_id !== t?.current_revision_id,
  )?.revision_id;
  const previous =
    revisions.data?.items.find((v) => String(v.id) === compare) ??
    revisions.data?.items.find((v) => v.id === lastApprovedID) ??
    revisions.data?.items.filter((v) => v.id !== t?.current_revision_id)[0];
  const act = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError('');
    setDone('');
    try {
      await fn();
      setDone(message);
      requestKey.current = '';
      r.reload();
      revisions.reload();
      reviews.reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const review = (decision: string) => {
    if (!t) return;
    if (!requestKey.current) requestKey.current = crypto.randomUUID();
    return act(
      () => api.reviewTask(t, decision, note, internal, requestKey.current),
      decision === 'approved' ? '已批准发布' : '已驳回，公开原因已发送商户',
    );
  };
  return (
    <div className="space-y-5">
      <Link className="text-primary text-sm" to="/agent-tasks">
        返回商户任务
      </Link>
      <Notice error={r.error || error} />
      {done && (
        <p
          role="status"
          className="rounded-md bg-green-50 p-3 text-sm text-green-700"
        >
          {done}
        </p>
      )}
      {t && (
        <>
          <Panel title={t.payload.title || '未命名任务'}>
            <div className="flex flex-wrap gap-4 text-sm">
              <Badge value={t.execution_status || t.status} />
              <span>商户：{merchants.name(t)}</span>
              {merchants.failed && <button className="text-primary" onClick={merchants.reload}>重试加载商户名称</button>}
            </div>
            <p className="text-sm text-gray-500">
              审核针对当前不可变修订；过期版本会被服务器拒绝。变更中的线上任务停止新领取，已领取记录继续按原快照审核。
            </p>
          </Panel>
          {t.execution && <Panel title="执行与会员奖励"><div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">{[["已完成", t.execution.completed_count], ["剩余待完成", t.execution.remaining_count], ["执行中", t.execution.in_flight_count], ["待审核", t.execution.pending_review_count], ["奖励已发放", t.execution.reward_credited_count], ["奖励待发放", t.execution.reward_pending_count]].map(([label, value]) => <p key={label}>{label}：<strong>{value}</strong> 份</p>)}</div><p className="text-xs text-gray-500">以上结算仅指会员任务奖励，商户商务结算暂未开放。</p></Panel>}
          {canControl && (
            <Panel title="线上任务控制">
              <div className="flex flex-wrap gap-3">
                <AgentTaskControls task={t} onChanged={() => { r.reload(); display.reload(); }} />
              </div>
              <p className="text-xs text-gray-500">
                暂停后停止新领取，已领取记录继续按原流程提交与审核。恢复时会校验已批准版本；待审核内容不能通过状态变更上线。
              </p>
              {t.formal_task_id && (
                <div className="flex flex-wrap gap-3 items-center border-t border-gray-100 pt-4">
                  <span className="text-sm">前端展示设置</span>
                  <Notice error={display.error} />
                  <input
                    aria-label="任务排序"
                    type="number"
                    className={`${input} w-28`}
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                  />
                  <label className="text-sm">
                    <input
                      type="checkbox"
                      checked={recommended}
                      onChange={(e) => setRecommended(e.target.checked)}
                    />{' '}
                    推荐
                  </label>
                  <button
                    className={button}
                    disabled={
                      busy || !display.data || !Number.isInteger(Number(sort))
                    }
                    onClick={() =>
                      act(
                        () =>
                          api.presentation(
                            t.formal_task_id!,
                            Number(sort),
                            recommended,
                          ),
                        '展示设置已保存',
                      )
                    }
                  >
                    仅保存排序 / 推荐
                  </button>
                </div>
              )}
            </Panel>
          )}
          <Panel title="会员端内容预览">
            <div className="grid gap-5 md:grid-cols-[1fr_220px]">
              <div className="space-y-4">
                <h3 className="font-semibold">
                  {t.payload.description_title || '任务说明'}
                </h3>
                <TextContent value={t.payload.description} />
                <h3 className="font-semibold">
                  {t.payload.rules_title || '任务规则'}
                </h3>
                <TextContent value={t.payload.rules} />
                {t.payload.steps?.map((s, i) => (
                  <div
                    className="rounded-md border border-gray-200 p-4 space-y-2"
                    key={s.id || i}
                  >
                    <h4 className="text-sm font-medium">
                      {i + 1}. {s.title}
                    </h4>
                    <TextContent value={s.description} />
                    <div className="flex flex-wrap gap-2">
                      {s.images?.map((src, j) => (
                        <img
                          key={j}
                          src={resolveImagePreviewUrl(src, t.asset_urls) || resolveAdminImagePreviewUrl(src) || undefined}
                          alt={`步骤 ${i + 1} 图片 ${j + 1}`}
                          className="h-24 w-24 rounded object-cover"
                        />
                      ))}
                    </div>
                    <TaskVideoPreview video={s.videos?.[0]} assetURLs={t.asset_urls} />
 {s.links?.map((l, j) => (
                      <a
                        key={j}
                        href={/^https?:\/\//i.test(l.url) ? l.url : undefined}
                        rel="noreferrer"
                        target="_blank"
                        className="block text-sm text-primary"
                      >
                        {l.label || l.url}
                      </a>
                    ))}
                  </div>
                ))}
                <h3 className="font-semibold">
                  {t.payload.review_rules_title || '审核要求'}
                </h3>
                <TextContent value={t.payload.review_rules} />
                {t.payload.submission_requirements && (
                  <>
                    <h3 className="font-semibold">提交要求</h3>
                    <TextContent value={t.payload.submission_requirements} />
                  </>
                )}
                {t.payload.remarks && (
                  <>
                    <h3 className="font-semibold">备注</h3>
                    <TextContent value={t.payload.remarks} />
                  </>
                )}
                {t.payload.required_screenshots?.length ? (
                  <div className="space-y-2">
                    <h3 className="font-semibold">截图要求</h3>
                    {t.payload.required_screenshots.map((x, i) => (
                      <p key={i} className="text-sm text-gray-600">
                        {i + 1}. {x.title || x.key} {x.required?'（必传）':'（选传）'} {x.description}
                      </p>
                    ))}
                  </div>
                ) : null}
                {t.payload.download_app && (
                  <div className="rounded-md border border-gray-200 p-4 text-sm space-y-2">
                    <h3 className="font-semibold">下载应用</h3>
                    <p>
                      {t.payload.download_app.name} ·{' '}
                      {t.payload.download_app.version} · App ID{' '}
                      {t.payload.download_app.id}
                    </p>
                    <p>Bundle ID：{t.payload.download_app.bundle_id}</p>
                    <p>
                      搜索词：
                      {t.payload.download_app.search_keywords?.join('、') ||
                        '—'}
                    </p>
                    <p>
                      各词名额：
                      {t.payload.download_app.search_keyword_quotas
                        ?.map((x) => `${x.keyword} ${x.target_count}`)
                        .join('、') || '—'}
                    </p>
                    <p>价格：{t.payload.download_app.formatted_price}</p>
                  </div>
                )}
                {t.payload.message_config && (
                  <div className="rounded-md border border-gray-200 p-4 text-sm space-y-2">
                    <h3 className="font-semibold">iMessage 任务配置</h3>
                    <p>活动类型：{({transactional_notice: "交易通知", service_update: "服务更新", consented_research_invite: "已授权调研邀请", consented_promotion: "已授权推广"} as Record<string, string>)[t.payload.message_config.campaign_type] || t.payload.message_config.campaign_type}</p>
                    <p>
                      授权凭据：
                      {t.payload.message_config.authorization_reference}
                    </p>
                    <p>
                      人工复核：
                      {t.payload.message_config.manual_approval_required
                        ? '是'
                        : '否'}
                    </p>
                  </div>
                )}
              </div>
              <aside className="space-y-3 rounded-md bg-gray-50 p-4 text-sm">
                {t.payload.icon_url && <img
                  src={resolveImagePreviewUrl(t.payload.icon_url, t.asset_urls) || resolveAdminImagePreviewUrl(t.payload.icon_url) || undefined}
                  alt="任务图标"
                  className="h-16 w-16 rounded object-contain"
                />}
                <p>
                  奖励 ¥
                  {((t.payload.reward_amount_minor ?? 0) / 100).toFixed(2)}
                </p>
                <p>名额 {t.payload.max_assignments}</p>
                <p>平台 {({ios: "iPhone / iOS", web: "网页", all: "iOS 与网页"} as Record<string, string>)[t.payload.platform || ""] || t.payload.platform || "—"}</p>
                <p>类型 {({normal: "普通任务", other_normal: "其他普通任务", download: "下载任务", message_send: "iMessage 任务"} as Record<string, string>)[t.payload.task_type || ""] || t.payload.task_type || "—"}</p>

                <p>领取时限 {t.payload.assignment_timeout_seconds == null ? "—" : t.payload.assignment_timeout_seconds / 60} 分钟</p>
                <p>
                  会员提交{t.payload.require_review ? '需审核' : '自动处理'}
                </p>
              </aside>
            </div>
          </Panel>
          <Panel title="版本差异">
            <Notice error={revisions.error} />
            {!!revisions.data?.items.some((v) => v.id !== t.current_revision_id) && <select
              aria-label="对比历史版本"
              className={input}
              value={compare || String(previous?.id ?? '')}
              onChange={(e) => setCompare(e.target.value)}
            >
              {revisions.data?.items
                .filter((v) => v.id !== t.current_revision_id)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    修订 #{v.id} · V{v.revision_no} ·{' '}
                    {formatDateTime(v.created_at)}
                  </option>
                ))}
            </select>}
            {previous ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm text-left">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3">字段</th>
                      <th className="p-3">历史版本</th>
                      <th className="p-3">当前送审版本</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(
                      new Set([
                        ...Object.keys(previous.payload),
                        ...Object.keys(t.payload),
                      ]),
                    )
                      .filter(
                        (k) =>
                          JSON.stringify(
                            previous.payload[
                              k as keyof typeof previous.payload
                            ],
                          ) !==
                          JSON.stringify(
                            t.payload[k as keyof typeof t.payload],
                          ),
                      )
                      .map((k) => (
                        <tr key={k} className="border-b border-gray-100">
                          <td className="p-3">{fieldLabels[k] ?? k}</td>
                          <td className="p-3 max-w-sm">
                            <pre className="whitespace-pre-wrap break-words text-xs text-red-700">
                              {shown(
                                previous.payload[
                                  k as keyof typeof previous.payload
                                ],
                              )}
                            </pre>
                          </td>
                          <td className="p-3 max-w-sm">
                            <pre className="whitespace-pre-wrap break-words text-xs text-green-700">
                              {shown(t.payload[k as keyof typeof t.payload])}
                            </pre>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                首次发布，暂无历史版本。请审核全部内容。
              </p>
            )}
          </Panel>
          {canReview && t.payload.message_config?.import_id && <AgentDraftMessageImport key={t.payload.message_config.import_id} taskId={t.id} />}
          {canReview && t.status === 'pending_review' && (
            <Panel title="发布审核">
              <label className="block text-sm">
                公开原因 / 审核说明
                <textarea
                  maxLength={2000}
                  className="mt-2 w-full rounded-md border border-gray-300 p-3"
                  placeholder="商户可见，驳回必须填写"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                内部备注
                <textarea
                  maxLength={2000}
                  className="mt-2 w-full rounded-md border border-gray-300 p-3"
                  placeholder="仅总后台可见"
                  value={internal}
                  onChange={(e) => setInternal(e.target.value)}
                />
              </label>
              <div className="flex gap-3">
                <button
                  className={primary}
                  disabled={busy}
                  onClick={() => review('approved')}
                >
                  批准当前版本并发布
                </button>
                <button
                  className={`${button} text-red-600`}
                  disabled={busy || !note.trim()}
                  onClick={() => review('rejected')}
                >
                  驳回并发送公开原因
                </button>
              </div>
            </Panel>
          )}
          <AgentMessagePool formalTaskId={t.formal_task_id} />
          <AgencySubmissions taskId={t.id} />
          <Panel title="审核历史">
            <Notice error={reviews.error} />
            {reviews.data?.items.length ? (
              reviews.data.items.map((x, i) => (
                <div className="text-sm rounded-md bg-gray-50 p-3" key={i}>
                  <div className="flex flex-wrap gap-3">
                    <Badge value={String(x.decision ?? '')} />
                    <span>修订 #{String(x.revision_id ?? '—')}</span>
                    <span>审核人 #{String(x.admin_id ?? '—')}</span>
                    <span>
                      {x.created_at
                        ? formatDateTime(String(x.created_at))
                        : '—'}
                    </span>
                  </div>
                  <p className="mt-2">公开说明：{String(x.note || '无')}</p>
                  <p className="mt-1 text-gray-500">
                    内部备注：{String(x.internal_note || '无')}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">暂无审核记录</p>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
